import { useEffect, useRef, useState } from "react";
import { Play, Pause, Music4, Lock, ShoppingCart, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";

// TrackPreviewPlayer — plays the auto-generated 60-second preview for a paid track.
// Enforces the 60s ceiling client-side too (belt-and-braces alongside the server-side clip).
// Full track only unlocks after payment is confirmed.
const PREVIEW_LIMIT_SECONDS = 60;

const fmt = (s) => {
  if (!s || Number.isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

const TrackPreviewPlayer = ({
  previewUrl,
  trackName,
  onBuyClick,
  price,
  currencySymbol = "$",
  productId,
  // Full-length mode (Mantra Suite etc.): raise the ceiling, relabel the kicker,
  // and drop the buy / share CTAs. Defaults keep the 60s paid-preview behaviour.
  limitSeconds = PREVIEW_LIMIT_SECONDS,
  kicker,
  hideCta = false,
  icon: Icon = Music4,
  testId = "track-preview-player",
  playTestId = "preview-play-btn",
  seekTestId = "preview-seek-bar",
}) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0-1
  const [current, setCurrent] = useState(0);
  const [hitLimit, setHitLimit] = useState(false);
  const limit = Math.max(1, Number(limitSeconds) || PREVIEW_LIMIT_SECONDS);

  const shareUrl = productId ? `${window.location.origin}/products/${productId}` : window.location.href;

  const handleShare = async () => {
    // Native share (mobile) if available, otherwise clipboard.
    try {
      if (navigator.share) {
        await navigator.share({
          title: trackName || "StardomFans track",
          text: `🎧 Take a 60-second listen to ${trackName || "this track"} on StardomFans.com`,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Preview link copied — paste it anywhere.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't share right now.");
    }
  };

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => {
      setCurrent(el.currentTime);
      setProgress(el.currentTime / limit);
      // Client-side ceiling — stop playback exactly at the limit no matter what the file duration is
      if (el.currentTime >= limit) {
        el.pause();
        el.currentTime = 0;
        setPlaying(false);
        setProgress(0);
        setCurrent(0);
        setHitLimit(!hideCta);
      }
    };
    const onEnd = () => { setPlaying(false); setProgress(0); setCurrent(0); };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnd);
    };
  }, [limit, hideCta]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      setHitLimit(false);
      el.play().catch(() => {});
      setPlaying(true);
    }
  };

  const seek = (pct) => {
    const el = audioRef.current;
    if (!el) return;
    const t = Math.max(0, Math.min(limit - 0.1, pct * limit));
    el.currentTime = t;
    setCurrent(t);
    setProgress(t / limit);
  };

  if (!previewUrl) return null;

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const src = previewUrl.startsWith("http") ? previewUrl : `${backendBase}${previewUrl}`;

  return (
    <div className="card p-5 sm:p-6 bg-gradient-to-br from-[#EC008C]/[0.08] via-[#0A0E18] to-transparent border-[#EC008C]/30" data-testid={testId}>
      <audio ref={audioRef} src={src} preload="metadata" />
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-[#EC008C]/15 border border-[#EC008C]/30 flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#EC008C]" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">{kicker || "Preview · 60 seconds"}</p>
          <p className="text-sm text-white truncate max-w-[280px]">{trackName || "Track preview"}</p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={toggle}
          className="w-12 h-12 rounded-full bg-[#EC008C] text-black flex items-center justify-center hover:bg-[#FF3399] transition-colors flex-shrink-0 shadow-lg shadow-[#EC008C]/25"
          data-testid={playTestId}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>

        <div className="flex-1">
          <div
            className="h-2 rounded-full bg-[#1A2030] cursor-pointer overflow-hidden relative group"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              seek((e.clientX - rect.left) / rect.width);
            }}
            role="slider"
            aria-label="Seek"
            data-testid={seekTestId}
          >
            <div className="h-full bg-gradient-to-r from-[#EC008C] to-[#FF3399] transition-[width]" style={{ width: `${Math.min(100, progress * 100)}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-[#8A92A0] mt-1.5">
            <span>{fmt(current)}</span>
            <span>{fmt(limit)}</span>
          </div>
        </div>
      </div>

      {hitLimit && !hideCta && (
        <div className="mt-4 flex items-start gap-2.5 p-3 rounded-lg bg-[#EC008C]/[0.06] border border-[#EC008C]/25" data-testid="preview-hit-limit">
          <Lock className="w-4 h-4 text-[#EC008C] mt-0.5 flex-shrink-0" />
          <div className="text-xs text-[#8A92A0]">
            Preview ended. Unlock the full track — instant download the moment your payment is confirmed.
          </div>
        </div>
      )}

      {onBuyClick && (
        <Button
          onClick={onBuyClick}
          className="mt-4 w-full bg-[#EC008C] text-black hover:bg-[#FF3399] rounded-full h-11 font-bold"
          data-testid="preview-buy-btn"
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          Buy full track {price ? `— ${currencySymbol}${price}` : ""}
        </Button>
      )}

      {!hideCta && (
        <button
          onClick={handleShare}
          className="mt-2 w-full flex items-center justify-center gap-2 text-xs text-[#8A92A0] hover:text-white h-9 rounded-full border border-[#1A2030] hover:border-[#EC008C]/40 transition-colors"
          data-testid="preview-share-btn"
        >
          <Share2 className="w-3.5 h-3.5" />
          Share this preview — post on TikTok, Instagram, X, anywhere
        </button>
      )}
    </div>
  );
};

export default TrackPreviewPlayer;
